import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { X, ClipboardList, FileCheck, CreditCard, Key, Lock, BookOpen, Wrench, Circle, Stethoscope, History } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { useDeliveryProofForm } from '@/features/delivery-proof/hooks/useDeliveryProofForm';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import { FileUploadField } from '@/features/delivery-proof/components/FileUploadField';
import {
  DeliveryProofElementType,
  type DeliveryProofAttachment,
  type ElementItemDto,
} from '@/features/delivery-proof/types/delivery-proof.types';
import { toAttachment } from '@/features/delivery-proof/utils/attachments';
import type { WizardStepHandle } from '@/Components/shared/WizardLayout.types';

interface ElementData {
  checked: boolean;
  date?: string; // Pour MOT
  note?: string; // Pour les éléments à précision libre (emplacement de l'écrou antivol)
  attachment?: DeliveryProofAttachment;
}

interface PredefinedElement {
  type: DeliveryProofElementType;
  label: string;
  icon: React.ReactNode;
  hasDate: boolean;
  /** Libellé du champ de précision libre (`note`), absent si l'élément n'en propose pas */
  noteLabel?: string;
}

// Doit rester aligné sur la colonne `note` de pro_v2_delivery_proof_element,
// un varchar(255) : au-delà l'API refuse l'enregistrement.
const ELEMENT_NOTE_MAX_LENGTH = 255;

const ELEMENT_TOOLTIPS: Record<string, string> = {
  [DeliveryProofElementType.MOT]: "Cochez cette case pour attester que vous avez remis un exemplaire du contrôle technique lors de la livraison, ajoutez ensuite la date de ce dernier. Facultatif: vous pouvez également joindre une copie du document qui apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.CARD]: "Cochez cette case pour attester que vous avez remis la carte grise ou un certificat provisoire d'immatriculation lors de la livraison. Facultatif: vous pouvez également joindre une copie du document qui apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.DIAGNOSTIC]: "Cochez cette case pour attester que vous avez remis le rapport de diagnostic du véhicule lors de la livraison. Facultatif: vous pouvez également joindre une copie du rapport qui apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.HISTOVEC]: "Cochez cette case pour attester que vous avez remis le rapport HistoVec (historique officiel du véhicule) lors de la livraison. Facultatif: vous pouvez également joindre une copie du rapport qui apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.KEY]: "Cochez cette case pour attester que vous avez remis le double des clés lors de la livraison. Facultatif: vous pouvez également joindre une photo du double des clefs, cette photo apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.NUT]: "Cochez cette case pour attester que vous avez remis l'écrou antivol lors de la livraison. Facultatif: vous pouvez préciser son emplacement, qui figurera sur l'attestation, et joindre une photo de l'écrou antivol à son emplacement, cette photo apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.RECORD]: "Cochez cette case pour attester que vous avez remis le carnet d'entretien lors de la livraison. Facultatif: vous pouvez également joindre une photo du dernier entretien, cette photo apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.PROOF]: "Cochez cette case pour attester que vous avez remis une attestation de travaux lors de la livraison. Facultatif: vous pouvez également joindre une photo du document, cette photo apparaîtra en annexe de l'attestation de livraison.",
  [DeliveryProofElementType.WHEEL]: "Cochez cette case pour attester que vous avez remis une roue de secours ou un kit de réparation de roue lors de la livraison. Facultatif: vous pouvez également joindre une photo du dispositif, cette photo apparaîtra en annexe de l'attestation de livraison.",
};

const predefinedElements: PredefinedElement[] = [
  {
    type: DeliveryProofElementType.MOT,
    label: 'Contrôle technique',
    icon: <FileCheck className="tw-w-4 tw-h-4" />,
    hasDate: true,
  },
  {
    type: DeliveryProofElementType.CARD,
    label: 'Carte grise',
    icon: <CreditCard className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.DIAGNOSTIC,
    label: 'Rapport de diagnostic',
    icon: <Stethoscope className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.HISTOVEC,
    label: 'Rapport HistoVec',
    icon: <History className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.KEY,
    label: 'Double des clés',
    icon: <Key className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.NUT,
    label: 'Écrou antivol',
    icon: <Lock className="tw-w-4 tw-h-4" />,
    hasDate: false,
    noteLabel: 'Emplacement',
  },
  {
    type: DeliveryProofElementType.RECORD,
    label: 'Carnet d\'entretien',
    icon: <BookOpen className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.PROOF,
    label: 'Attestation de travaux',
    icon: <Wrench className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
  {
    type: DeliveryProofElementType.WHEEL,
    label: 'Roue de secours - kit de réparation roue',
    icon: <Circle className="tw-w-4 tw-h-4" />,
    hasDate: false,
  },
];

export const Step3ElementsSelection = forwardRef<WizardStepHandle>((_props, ref) => {
  const { nextStep, updateFormData } = useDeliveryProofForm();
  const deliveryProofId = useDeliveryProofStore((state) => state.deliveryProofId);
  const formData = useDeliveryProofStore((state) => state.formData);

  // État initial basé sur les données du store
  const [showElementsBlock, setShowElementsBlock] = useState(formData.elements.length > 0);

  // Map des éléments prédéfinis avec leurs données
  const [elementsData, setElementsData] = useState<Map<DeliveryProofElementType, ElementData>>(() => {
    const map = new Map<DeliveryProofElementType, ElementData>();
    formData.elements.forEach((el) => {
      if (el.type !== DeliveryProofElementType.CUSTOM) {
        map.set(el.type, {
          checked: true,
          date: el.date,
          note: el.note,
          attachment: toAttachment(el),
        });
      }
    });
    return map;
  });

  // Éléments personnalisés
  const [customElements, setCustomElements] = useState<Array<{ label: string; attachment?: DeliveryProofAttachment }>>(() => {
    return formData.elements
      .filter((el) => el.type === DeliveryProofElementType.CUSTOM && el.label)
      .map((el) => ({ label: el.label!, attachment: toAttachment(el) }));
  });

  const [customInputValue, setCustomInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [motDateError, setMotDateError] = useState(false);

  const handleElementToggle = (type: DeliveryProofElementType) => {
    setElementsData((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(type);
      if (current?.checked) {
        newMap.delete(type);
      } else {
        newMap.set(type, { checked: true });
      }
      return newMap;
    });
  };

  const handleElementDate = (type: DeliveryProofElementType, date: string) => {
    if (type === DeliveryProofElementType.MOT) setMotDateError(false);
    setElementsData((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(type);
      if (current) {
        newMap.set(type, { ...current, date });
      }
      return newMap;
    });
  };

  const handleElementNote = (type: DeliveryProofElementType, note: string) => {
    setElementsData((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(type);
      if (current) {
        newMap.set(type, { ...current, note });
      }
      return newMap;
    });
  };

  const handleElementFile = (type: DeliveryProofElementType, attachment: DeliveryProofAttachment | undefined) => {
    setElementsData((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(type);
      if (current) {
        newMap.set(type, { ...current, attachment });
      }
      return newMap;
    });
  };

  const handleAddCustomElement = () => {
    const trimmed = customInputValue.trim();
    if (!trimmed) return;

    if (customElements.some((el) => el.label.toLowerCase() === trimmed.toLowerCase())) {
      setError('Cet élément existe déjà');
      return;
    }

    setCustomElements((prev) => [...prev, { label: trimmed }]);
    setCustomInputValue('');
    setError(null);
  };

  const handleRemoveCustomElement = (index: number) => {
    setCustomElements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCustomElementFile = (index: number, attachment: DeliveryProofAttachment | undefined) => {
    setCustomElements((prev) =>
      prev.map((el, i) => (i === index ? { ...el, attachment } : el))
    );
  };

  // Synchronise l'état local vers le store
  const syncToStore = () => {
    const elements: ElementItemDto[] = [];

    if (showElementsBlock) {
      elementsData.forEach((data, type) => {
        if (data.checked) {
          elements.push({
            type,
            date: data.date,
            note: data.note?.trim() || undefined,
            ...data.attachment,
          });
        }
      });

      customElements.forEach((el) => {
        elements.push({
          type: DeliveryProofElementType.CUSTOM,
          label: el.label,
          ...el.attachment,
        });
      });
    }

    updateFormData({ elements });
  };

  // Sync live vers le store pour la preview PDF
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    syncToStore();
  }, [elementsData, customElements, showElementsBlock]);

  useImperativeHandle(ref, () => ({
    handleNext: () => handleSubmit(),
    handleSave: () => syncToStore(),
  }));

  // Suivant : navigation locale uniquement (pas d'appel API)
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validation : date obligatoire si contrôle technique coché
    if (showElementsBlock) {
      const motData = elementsData.get(DeliveryProofElementType.MOT);
      if (motData?.checked && !motData.date) {
        setMotDateError(true);
        return;
      }
    }

    syncToStore();
    nextStep();
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-2 sm:tw-p-4">
      <div className="tw-p-4 sm:tw-p-6 tw-space-y-5">
      <form id="wizard-step-form" onSubmit={handleSubmit} className="tw-space-y-6">
        {/* Checkbox d'activation du bloc */}
        <label id="dp-elements-toggle" className="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-select-none">
          <input
            type="checkbox"
            checked={showElementsBlock}
            onChange={(e) => setShowElementsBlock(e.target.checked)}
            className="tw-mt-0.5 tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
          />
          <span className="tw-text-sm tw-text-gray-700">
            Intégrer le bloc &quot;Éléments remis&quot; dans l'attestation de livraison (facultatif)
          </span>
        </label>

        {showElementsBlock && (
          <div id="dp-elements-list">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-pb-3 tw-border-b tw-border-gray-100">
              <ClipboardList className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
              <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Éléments remis à la livraison</h4>
            </div>

            <div className="tw-space-y-3">
              {/* Checkboxes prédéfinies avec sous-champs inline */}
              {predefinedElements.map((element) => {
                const data = elementsData.get(element.type);
                const isChecked = !!data?.checked;
                const tooltip = ELEMENT_TOOLTIPS[element.type];
                // Date ou précision : un champ à gauche de la pièce jointe
                const hasSideField = element.hasDate || !!element.noteLabel;

                return (
                  <div key={element.type} className="tw-space-y-2">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleElementToggle(element.type)}
                          className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
                        />
                        <span className="tw-text-gray-600">{element.icon}</span>
                        <span className="tw-text-sm tw-text-gray-900">{element.label}</span>
                      </label>
                      {tooltip && (
                        <Tooltip content={tooltip} />
                      )}
                    </div>

                    {/* Sous-champs inline quand coché */}
                    {isChecked && (
                      <div className="tw-ml-6 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                        <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-items-start">
                          {element.hasDate && (
                            <div className="tw-col-span-12 md:tw-col-span-4">
                              <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Date du contrôle technique <span className="tw-text-red-500">*</span></label>
                              <input
                                type="date"
                                required
                                value={data?.date || ''}
                                onChange={(e) => handleElementDate(element.type, e.target.value)}
                                className={`tw-block tw-w-full tw-px-3 tw-py-2 tw-text-base sm:tw-text-sm tw-border tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary ${motDateError && !data?.date ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                              />
                            </div>
                          )}
                          {element.noteLabel && (
                            <div className="tw-col-span-12 md:tw-col-span-4">
                              <label
                                htmlFor={`dp-element-note-${element.type}`}
                                className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block"
                              >
                                {element.noteLabel} (facultatif)
                              </label>
                              <input
                                id={`dp-element-note-${element.type}`}
                                type="text"
                                maxLength={ELEMENT_NOTE_MAX_LENGTH}
                                value={data?.note || ''}
                                onChange={(e) => handleElementNote(element.type, e.target.value)}
                                placeholder="Ex. : boîte à gants"
                                className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-base sm:tw-text-sm tw-border tw-border-gray-200 tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                              />
                            </div>
                          )}
                          <div className={`tw-col-span-12 ${hasSideField ? 'md:tw-col-span-8' : ''}`}>
                            <FileUploadField
                              deliveryProofId={deliveryProofId!}
                              value={data?.attachment}
                              onChange={(attachment) => handleElementFile(element.type, attachment)}
                              size="sm"
                              label={element.hasDate ? 'Justificatif (facultatif)' : element.noteLabel ? 'Photo (facultatif)' : undefined}
                            />
                          </div>
                        </div>
                        {element.hasDate && motDateError && !data?.date && (
                          <p className="tw-text-xs tw-text-red-600 tw-mt-1">La date du contrôle technique est obligatoire</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Éléments personnalisés */}
              {customElements.map((el, index) => (
                <div key={`custom-${index}`} className="tw-space-y-2">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <label className="tw-flex tw-items-center tw-gap-2 tw-select-none">
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        className="tw-w-4 tw-h-4 tw-accent-affinicar-primary"
                      />
                      <span className="tw-text-sm tw-text-gray-900">{el.label}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomElement(index)}
                      className="tw-text-red-400 hover:tw-text-red-600 tw-p-0.5 tw-transition-colors"
                      title="Supprimer"
                    >
                      <X className="tw-w-3.5 tw-h-3.5" />
                    </button>
                  </div>
                  <div className="tw-ml-6 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                    <FileUploadField
                      deliveryProofId={deliveryProofId!}
                      value={el.attachment}
                      onChange={(attachment) => handleCustomElementFile(index, attachment)}
                      size="sm"
                    />
                  </div>
                </div>
              ))}

              {/* Ajout élément personnalisé */}
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch sm:tw-items-center tw-gap-2 tw-mt-4">
                <input
                  type="text"
                  value={customInputValue}
                  onChange={(e) => { setCustomInputValue(e.target.value); setError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomElement(); } }}
                  placeholder="Autre..."
                  className="tw-flex-1 tw-px-3 tw-py-2.5 tw-text-base sm:tw-text-sm tw-border tw-border-gray-200 tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                />
                <button
                  type="button"
                  onClick={handleAddCustomElement}
                  disabled={!customInputValue.trim()}
                  className="tw-px-4 tw-py-2.5 tw-bg-affinicar-primary tw-text-white tw-rounded-xl tw-text-sm tw-font-medium hover:tw-bg-affinicar-dark tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex-shrink-0"
                >
                  Ajouter
                </button>
              </div>

              {error && (
                <p className="tw-text-sm tw-text-red-600">{error}</p>
              )}
            </div>
          </div>
        )}
      </form>

      </div>
    </div>
  );
});

Step3ElementsSelection.displayName = 'Step3ElementsSelection';
